import { TypographyCaptionBold } from '@heidi/design-system/typography';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@heidi/design-system/tooltip';
import { cn } from '@/lib/cn';
import {
  LINE_HEIGHT,
  MAX_LINE_LENGTH,
  truncateForPreview,
} from './chatQuestionNavUtils';

interface ChatQuestionNavLineProps {
  messageId: string;
  messageContent: string;
  index: number;
  isActive: boolean;
  onScrollToMessage: () => void;
}

/**
 * Simple hash to get a consistent pseudo-random value from a string.
 * Returns a value between 0 and 1.
 */
const hashToRatio = (str: string): number => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash % 100) / 100;
};

export const ChatQuestionNavLine = ({
  messageId,
  messageContent,
  index,
  isActive,
  onScrollToMessage,
}: ChatQuestionNavLineProps) => {
  // 60-80% of max width for visual variety
  const ratio = 0.6 + hashToRatio(messageId) * 0.2;
  const width = Math.round(MAX_LINE_LENGTH * ratio);

  return (
    <Tooltip disableHoverableContent>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={onScrollToMessage}
          className={cn(
            'group/line flex w-full justify-end py-1.5',
            'focus-visible:rounded focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring'
          )}
          aria-label={`Jump to question ${index + 1}: ${messageContent.slice(0, 50)}`}
        >
          <div
            className={cn(
              'origin-right rounded-full transition-all duration-150',
              'motion-safe:group-hover/line:scale-[1.3]',
              isActive ? 'bg-text-secondary' : 'bg-border group-hover/line:bg-text-tertiary'
            )}
            style={{
              width: `${width}px`,
              height: `${LINE_HEIGHT}px`,
            }}
          />
        </button>
      </TooltipTrigger>
      <TooltipContent
        side="left"
        align="center"
        sideOffset={8}
        className="z-50 max-w-[200px] rounded-lg !border-[0.5px] !border-solid !border-border bg-surface p-2.5 text-xs text-text-primary shadow-lg"
      >
        <TypographyCaptionBold className="text-text-secondary">{truncateForPreview(messageContent)}</TypographyCaptionBold>
      </TooltipContent>
    </Tooltip>
  );
};

ChatQuestionNavLine.displayName = 'ChatQuestionNavLine';
